import React, { useState } from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { DocumentsParamList } from '../../../typings/Params';
import Filters from '../../../components/Filter/FilterIndex/FilterIndex';
import { translate } from '../../../services/translate';
import { useAuth } from '../../../context/Auth';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import CollapsibleFilter from '../../../components/Filters/Dispatch/CollapsibleFilter';
import FilterDependencyMessage from '../../../components/Filters/FilterDependencyMessage';
import ActingBodiesList from '../../../components/Filters/Dispatch/ActingBodiesList';
import ActingBodyUnitsList from '../../../components/Filters/Dispatch/ActingBodyUnitsList';

interface Props {
    filters: DocumentsParamList['DocumentCategories'];
    setFilters: React.Dispatch<React.SetStateAction<DocumentsParamList['DocumentCategories']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

export default function Filter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const navigation = useNavigation<NavigationProp<DocumentsParamList, 'DocumentCategories'>>();
    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [selectedActingBodyId, setSelectedActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [selectedActingBodyUnitIds, setSelectedActingBodyUnitIds] = useState<number[]>(filters.actingBodyUnitIds ?? []);

    const [openFilter, setOpenFilter] = useState<string | null>(null);

    const actingBodyId = getActingBodyId();
    const needsActingBody = actingBodyId === 0;

    const totalActiveFilters = [
        selectedActingBodyId !== 0 ? 1 : 0,
        selectedActingBodyUnitIds.length > 0 ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.documents_manage_all_acting_bodies_documents) {
            return selectedActingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    const toggleFilter = (key: string) => {
        setOpenFilter(prev => prev === key ? null : key);
    };

    const handleCleanFilter = () => {
        setOpenFilter(null);
        setTextFilter('');
        setSelectedActingBodyId(0);
        setSelectedActingBodyUnitIds([]);
    };

    const handleFilter = () => {
        const params = {
            page: 1 as const,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            actingBodyUnitIds: selectedActingBodyUnitIds.length > 0 ? selectedActingBodyUnitIds : undefined,
        };

        navigation.setParams({
            ...filters,
            ...params,
        });

        setFilters({
            ...filters,
            ...params,
            page: 0,
        });
    };

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter} filterCount={totalActiveFilters}>

            <MyAppText style={styles.sectionTitle(0)}>
                {translate('organizational')}
            </MyAppText>

            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.documents_manage_all_acting_bodies_documents) &&
                <CollapsibleFilter
                    label={translate('actingBody')}
                    isOpen={openFilter === 'actingBody'}
                    onToggle={() => toggleFilter('actingBody')}
                    count={selectedActingBodyId !== 0 ? 1 : 0}
                >
                    <ActingBodiesList value={selectedActingBodyId} setValue={setSelectedActingBodyId} />
                </CollapsibleFilter>
            }

            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.documents_manage_acting_body_units_documents || authenticatedUser?.permissions.documents_manage_all_acting_bodies_documents) &&
                <CollapsibleFilter
                    label={translate('actingBodyUnit')}
                    isOpen={openFilter === 'actingBodyUnit'}
                    onToggle={() => toggleFilter('actingBodyUnit')}
                    count={selectedActingBodyUnitIds.length}
                >
                    {needsActingBody
                        ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                        : <ActingBodyUnitsList value={selectedActingBodyUnitIds} setValue={setSelectedActingBodyUnitIds} actingBodyId={actingBodyId} />
                    }
                </CollapsibleFilter>
            }
        </Filters>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    sectionTitle: (padding: number = 16) => ({
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500'],
        paddingTop: padding,
        textTransform: 'uppercase',
    }),
}));
